/**
 * Stand-ins that let a Shadygram shader compile on every other ISF host.
 *
 * Shadygram injects camera/tracking/audio helpers (`getCameraColor`,
 * `getSegmentation`, …) and `u_*` built-ins ahead of a shader. The contract is
 * that a shader only touches them inside `#ifdef SHADYGRAM_HOST`, so VDMX,
 * Resolume and Hypno 2 skip that code. But shaders are written by a model and
 * by hand, and one unguarded call is not "degraded" elsewhere — it is a compile
 * error and a black output on stage ("no function with name 'getUV'").
 *
 * Rather than hope the contract is always kept, the file carries its own
 * safety net: for exactly the extensions it uses outside a guard, a block of
 * definitions under `#ifndef SHADYGRAM_HOST`. Shadygram never sees them (its
 * real helpers win); every other host gets something that compiles and reads
 * as "no camera, nobody in frame, silence".
 */

import { findHeaderSpan } from './scan';
import { collectIdentifiers } from './glsl';
import { withoutHostGuards } from './hostGuards';
import { SHADYGRAM_HOST_MACRO } from './preamble';
import { NEUTRAL_CALLS, scalarSubstitutionFor, vectorSubstitutionFor } from './convert/degrade';

const BLOCK_BEGIN = '// >>> shadygram-host-fallbacks (generated: keeps this file compiling on other ISF hosts)';
const BLOCK_END = '// <<< shadygram-host-fallbacks';
// Exactly what withHostFallbacks inserts — newline, block, newline — so stripping restores the
// author's text byte for byte (the two-way mirror compares content hashes).
const BLOCK_RE = new RegExp(`\\n${escapeRe(BLOCK_BEGIN)}[\\s\\S]*?${escapeRe(BLOCK_END)}\\n`, 'g');

function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface HelperSignature {
  returns: string;
  params: string;
  /** Overrides the converter's neutral value where a stand-alone host needs a different one. */
  value?: string;
}

/**
 * Every function Shadygram injects. Values come from the legacy converter's
 * NEUTRAL_CALLS unless stated: there the camera becomes a real `inputImage`,
 * so "everyone is the subject" (1.0) keeps the picture; here there is no camera
 * at all, and a mask of 1.0 would paint the stand-in black camera over the whole
 * frame. No camera means nobody in frame.
 */
const HELPERS: Record<string, HelperSignature> = {
  getUV: { returns: 'vec2', params: '', value: 'isf_FragNormCoord' },
  getCoord: { returns: 'vec2', params: '', value: 'gl_FragCoord.xy' },
  screenUVToCamUV: { returns: 'vec2', params: 'vec2 uv', value: 'uv' },
  camUVToScreenUV: { returns: 'vec2', params: 'vec2 uv', value: 'uv' },
  getCameraColor: { returns: 'vec4', params: 'vec2 uv', value: 'vec4(0.0)' },
  getCameraColorRaw: { returns: 'vec4', params: 'vec2 uv', value: 'vec4(0.0)' },
  getFFT: { returns: 'float', params: 'float pos', value: '0.0' },
  getWaveform: { returns: 'float', params: 'float pos', value: '0.0' },
  getAudioBand: { returns: 'float', params: 'float from, float to', value: '0.0' },
  getFaceLandmark: { returns: 'vec3', params: 'float index' },
  getHandLandmark: { returns: 'vec3', params: 'float index' },
  getHandLandmarkH: { returns: 'vec3', params: 'float index, float hand' },
  getPoseLandmark: { returns: 'vec3', params: 'float index' },
  getHandWorldLandmark: { returns: 'vec4', params: 'float index' },
  getHandWorldLandmarkH: { returns: 'vec4', params: 'float index, float hand' },
  getPoseWorldLandmark: { returns: 'vec4', params: 'float index' },
  getHandSide: { returns: 'float', params: 'float hand' },
  getSegmentation: { returns: 'float', params: 'vec2 uv', value: '0.0' },
  getHairMask: { returns: 'float', params: 'vec2 uv' },
  getSkinMask: { returns: 'float', params: 'vec2 uv' },
  getFaceMask: { returns: 'float', params: 'vec2 uv' },
  getClothesMask: { returns: 'float', params: 'vec2 uv' },
  getBodyDepth: { returns: 'float', params: 'vec2 uv' },
  getHandDepth: { returns: 'float', params: 'vec2 uv' },
};

/** Built-ins ISF already has a name for: an alias, not a dead uniform. */
const ALIASES: Record<string, string> = {
  u_time: 'TIME',
  u_resolution: 'RENDERSIZE',
  u_aspect: '(RENDERSIZE.x / RENDERSIZE.y)',
};

/** Built-ins that are neither a float nor a vec3 (those two are read off the converter's tables). */
const UNIFORM_TYPES: Record<string, string> = {
  u_camera: 'sampler2D',
  u_cameraResolution: 'vec2',
  u_cameraMirror: 'float',
  u_cameraMatrix: 'mat4',
  u_faceLandmarks: 'sampler2D',
  u_handLandmarks: 'sampler2D',
  u_handWorldLandmarks: 'sampler2D',
  u_handHandedness: 'vec2',
  u_poseLandmarks: 'sampler2D',
  u_poseWorldLandmarks: 'sampler2D',
  u_segmentationMask: 'sampler2D',
  u_segmentHair: 'sampler2D',
  u_segmentSkin: 'sampler2D',
  u_segmentFace: 'sampler2D',
  u_segmentClothes: 'sampler2D',
  u_audioFFT: 'sampler2D',
  u_audio: 'sampler2D',
  u_audioBins: 'float',
  u_audioLevel: 'float',
  u_audioActive: 'float',
  u_texCount: 'float',
};

function uniformTypeOf(name: string): string | null {
  if (UNIFORM_TYPES[name]) return UNIFORM_TYPES[name];
  if (scalarSubstitutionFor(name)) return 'float';
  if (vectorSubstitutionFor(name)) return 'vec3';
  return null;
}

/** True when the shader declares `name` itself (its own function, uniform or #define). */
function definesItself(body: string, name: string): boolean {
  const id = escapeRe(name);
  return new RegExp(`\\b(?:void|bool|int|float|vec[234]|mat[234]|sampler2D)\\s+${id}\\s*[(;=,]|#define\\s+${id}\\b`).test(body);
}

/** The source with any generated fallback block removed: the code as its author wrote it. */
export function stripHostFallbacks(source: string): string {
  return source.replace(BLOCK_RE, '');
}

export interface HostFallbackResult {
  source: string;
  /** Extensions that were given a stand-in, for telling the author. Empty when the file was already portable. */
  stubbed: string[];
}

interface FallbackPlan {
  /** The generated block, or '' when the code keeps the guard contract. */
  block: string;
  stubbed: string[];
}

function planFallbacks(body: string): FallbackPlan {
  const exposed = withoutHostGuards(body);
  const lines: string[] = [];
  const stubbed: string[] = [];
  for (const name of collectIdentifiers(exposed)) {
    if (definesItself(exposed, name)) continue;
    const helper = HELPERS[name];
    if (helper) {
      lines.push(`${helper.returns} ${name}(${helper.params}) { return ${helper.value ?? NEUTRAL_CALLS[name]}; }`);
      stubbed.push(`${name}()`);
    } else if (ALIASES[name]) {
      lines.push(`#define ${name} ${ALIASES[name]}`);
    } else {
      const type = uniformTypeOf(name);
      if (!type) continue;
      lines.push(`uniform ${type} ${name};`);
      stubbed.push(name);
    }
  }
  if (lines.length === 0) return { block: '', stubbed };
  return {
    block: [BLOCK_BEGIN, `#ifndef ${SHADYGRAM_HOST_MACRO}`, ...lines.sort(), '#endif', BLOCK_END].join('\n'),
    stubbed: stubbed.sort(),
  };
}

/**
 * The block a file written from `source` would carry ('' for none). Part of a
 * mirrored file's identity: it is generated, so a change to it — a shader that
 * starts needing stand-ins, a new stand-in value — must rewrite the file even
 * though the author's code did not change.
 */
export function hostFallbackBlock(source: string): string {
  const header = findHeaderSpan(source);
  return header ? planFallbacks(stripHostFallbacks(source.slice(header.commentEnd))).block : '';
}

/**
 * Ensure the fallback block matches what the shader needs. Idempotent: the
 * block is regenerated from the body every time, so it appears, changes and
 * disappears with the code — and a shader that keeps the guard contract gets
 * no block at all. Non-ISF sources are returned untouched.
 */
export function withHostFallbacks(source: string): HostFallbackResult {
  const header = findHeaderSpan(source);
  if (!header) return { source, stubbed: [] };

  const head = source.slice(0, header.commentEnd);
  const body = stripHostFallbacks(source.slice(header.commentEnd));
  const { block, stubbed } = planFallbacks(body);
  // Nothing exposed: a stale block is dropped; without one the file comes back byte-identical.
  if (!block) return { source: head + body, stubbed };
  return { source: `${head}\n${block}\n${body}`, stubbed };
}
